import { supporterEapArrearsQuery } from "@/api/reporting";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { DataTableHeader } from "@/components/data-table/DataTableHeader";
import { StyledLink } from "@/components/link/StyledLink";
import { Button } from "@/components/ui/Button";
import { DateRangePicker } from "@/components/ui/DateRangePicker";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { IdAndName, IdAndNameFilter } from "@/pages/supplier/sales/IdAndNameFilter";
import { formatShortDate } from "@common/utils/date";
import { formatDollars } from "@common/utils/i18n";
import { Box, Flex, QueryHandler, Span, Text, UnstyledButton } from "@givenwell/components";
import { CalendarIcon, CheckIcon, InformationCircleIcon, ReceiptIcon } from "@givenwell/core-solid-icons";
import { EapArrearDetails } from "@givenwell/management-api";
import { colors } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useLocation, useNavigate } from "@tanstack/react-router";
import { PropsWithChildren, ReactNode, useEffect } from "react";
import { array, fallback, InferOutput, object, optional, picklist, pipe, safeParse, string, transform } from "valibot";

const PaymentStates: IdAndName[] = [
  { id: "paid", name: "Paid" },
  { id: "unpaid", name: "Unpaid" },
];

const Sources: IdAndName[] = [
  { id: "purchase", name: "Purchases" },
  { id: "claim", name: "Claims" },
];

function optionalStringToOptionalDate(dateStr: string = "") {
  const date = new Date(dateStr);
  if (!isNaN(date.getTime())) {
    return date;
  }
  return undefined;
}

const SOURCES = ["purchase", "claim"] as string[];

/** Which supporter block comes first: the one transacting most recently, or the first alphabetically. */
const GroupOrderSchema = picklist(["recent", "supporter"]);

/**
 * What arrears actually means day to day: we have paid the supplier or the member, and the supporter
 * has not yet paid us. Anything wider is a deliberate widening, and the Reset button comes back here.
 */
const DEFAULT_FILTERS = {
  "supporter-ids": undefined,
  sources: SOURCES,
  currencies: undefined,
  "third-party-payment": ["paid"],
  "supporter-payment": ["unpaid"],
  start: undefined,
  end: undefined,
  "group-order": "recent",
} satisfies StoredFilters;

export const Route = createFileRoute("/admin/reports/$countryCode/eap-arrears")({
  validateSearch: object({
    "supporter-ids": optional(array(string())),
    sources: optional(array(string()), SOURCES),
    currencies: optional(array(string())),
    "third-party-payment": optional(array(string()), DEFAULT_FILTERS["third-party-payment"]),
    "supporter-payment": optional(array(string()), DEFAULT_FILTERS["supporter-payment"]),
    start: fallback(pipe(optional(string()), transform(optionalStringToOptionalDate)), undefined),
    end: fallback(pipe(optional(string()), transform(optionalStringToOptionalDate)), undefined),
    "group-order": fallback(optional(GroupOrderSchema, DEFAULT_FILTERS["group-order"]), "recent"),
  }),
  component: SupporterEapArrearsPage,
  beforeLoad: () => ({
    breadcrumb: "Supporter EAP Arrears",
  }),
});

/**
 * Filters live in the URL (so a view can be shared) but are also mirrored to localStorage, because
 * whoever is chasing arrears comes back to the same slice of the report every time. Arriving with no
 * search params at all restores the stored set; an explicit URL always wins.
 */
const FILTERS_STORAGE_KEY = "eap-arrears-filters:v1";

const StoredFiltersSchema = object({
  "supporter-ids": optional(array(string())),
  sources: optional(array(string())),
  currencies: optional(array(string())),
  "third-party-payment": optional(array(string())),
  "supporter-payment": optional(array(string())),
  start: optional(string()),
  end: optional(string()),
  "group-order": optional(GroupOrderSchema),
});

type StoredFilters = InferOutput<typeof StoredFiltersSchema>;

function readStoredFilters(): StoredFilters | undefined {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return undefined;

    const result = safeParse(StoredFiltersSchema, JSON.parse(raw));
    return result.success ? result.output : undefined;
  } catch {
    // Unreadable or stale shape: fall back to the defaults rather than breaking the page.
    return undefined;
  }
}

function writeStoredFilters(filters: StoredFilters) {
  try {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch (e) {
    console.error("Failed to save EAP arrears filters", e);
  }
}

function SupporterEapArrearsPage() {
  const eapArrearsQuery = useQuery(supporterEapArrearsQuery());
  const search = Route.useSearch();
  const searchStr = useLocation({ select: location => location.searchStr });
  const navigate = useNavigate();

  // Restore on a bare URL only, and only once — later navigations within the page always carry params.
  useEffect(() => {
    if (searchStr) return;

    const stored = readStoredFilters();
    if (!stored) return;

    void navigate({ to: ".", params: true, search: stored, replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!searchStr) return;

    writeStoredFilters({
      "supporter-ids": search["supporter-ids"],
      sources: search.sources,
      currencies: search.currencies,
      "third-party-payment": search["third-party-payment"],
      "supporter-payment": search["supporter-payment"],
      start: search.start?.toISOString(),
      end: search.end?.toISOString(),
      "group-order": search["group-order"],
    });
  }, [search, searchStr]);

  return (
    <PageWrapper title="Supporter EAP Arrears">
      <Box css={{ spaceY: 16, mt: 16 }}>
        <QueryHandler query={eapArrearsQuery} success={({ data }) => <EapArrearsTable records={data.records} />} />
      </Box>
    </PageWrapper>
  );
}

interface EapArrearsTableProps {
  records: EapArrearDetails[];
}

function EapArrearsTable({ records }: EapArrearsTableProps) {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const {
    "supporter-ids": supporterIds,
    sources,
    currencies,
    "third-party-payment": thirdPartyPayment,
    "supporter-payment": supporterPayment,
    start,
    end,
    "group-order": groupOrder,
  } = search;

  const distinctSupporters = Array.from(new Set(records.map(record => record.supporter_id))).map(supporter_id => ({
    id: supporter_id,
    name: records.find(record => record.supporter_id === supporter_id)?.supporter_name ?? "",
  }));

  const distinctCurrencies: IdAndName[] = Array.from(new Set(records.map(record => record.currency)))
    .sort()
    .map(currency => ({ id: currency, name: currency.toUpperCase() }));
  const allCurrencyIds = distinctCurrencies.map(currency => currency.id);
  const allSupporterIds = distinctSupporters.map(supporter => supporter.id);

  // The totals are struck before the currency filter so every currency keeps a card: they double as the
  // currency switch, and a card you have filtered away is a card you can no longer click.
  const recordsInOtherFilters = records.filter(
    searchFilter(supporterIds, sources, undefined, thirdPartyPayment, supporterPayment, start, end),
  );

  // This order is what the reader sees until they sort a column: DataTableBody leaves the given order
  // alone until a header is clicked, and groups take their place from where their first row landed.
  const filteredRecords = recordsInOtherFilters
    .filter(record => !currencies || currencies.includes(record.currency))
    .sort((a, b) => {
      if (groupOrder === "supporter") {
        const bySupporter = a.supporter_name.localeCompare(b.supporter_name);
        if (bySupporter !== 0) return bySupporter;
      }

      return new Date(b.event_time).getTime() - new Date(a.event_time).getTime();
    });

  return (
    <>
      <CurrencyTotals
        records={recordsInOtherFilters}
        selectedCurrencies={currencies ?? allCurrencyIds}
        onCurrencyClick={handleCurrencyClick}
      />
      <DataTable>
        <DataTableHeader
          hideSearch
          hideToggle
          hideClear
          rightContent={
            <Flex css={{ gap: 8 }}>
              <Button
                size="sm"
                variant="outlined"
                color="neutral"
                title="Changes the order of the supporter blocks"
                onClick={() => commit({ "group-order": groupOrder === "supporter" ? "recent" : "supporter" })}
              >
                {groupOrder === "supporter" ? "Supporters: A–Z" : "Supporters: most recent"}
              </Button>
              <Button size="sm" variant="outlined" color="neutral" onClick={() => commit(DEFAULT_FILTERS)}>
                Reset filters
              </Button>
            </Flex>
          }
        >
          <IdAndNameFilter
            label={describeSelection("Type", sources, Sources)}
            options={Sources}
            value={sources}
            onCommit={selected => commit({ sources: selected })}
          />
          <IdAndNameFilter
            label={describeSelection("Supporter", supporterIds ?? allSupporterIds, distinctSupporters)}
            options={distinctSupporters}
            value={supporterIds ?? allSupporterIds}
            onCommit={ids => commit({ "supporter-ids": ids })}
          />
          <IdAndNameFilter
            label={describeSelection("Currency", currencies ?? allCurrencyIds, distinctCurrencies)}
            options={distinctCurrencies}
            value={currencies ?? allCurrencyIds}
            onCommit={selected => commit({ currencies: selected })}
          />
          {/* Money flows out to the third party before it comes back from the supporter; the filters follow it. */}
          <IdAndNameFilter
            label={describePaymentSelection("3rd Party", thirdPartyPayment)}
            options={PaymentStates}
            value={thirdPartyPayment}
            onCommit={statuses => commit({ "third-party-payment": statuses })}
          />
          <IdAndNameFilter
            label={describePaymentSelection("Supporter", supporterPayment)}
            options={PaymentStates}
            value={supporterPayment}
            onCommit={statuses => commit({ "supporter-payment": statuses })}
          />
          <DateRangePicker
            // The picker renders the range itself once both ends are set; this covers the rest.
            placeholder={describeDateRange(start, end)}
            value={{ start, end }}
            onValueChange={({ start, end }) => {
              commit({ start: start?.toISOString(), end: end?.toISOString() });
            }}
            maxDate={new Date()}
          />
        </DataTableHeader>
        <DataTableBody
          rounded="bottom"
          data={filteredRecords}
          columns={[
            {
              // A gutter: it holds the group's caret, and leaving it empty on detail rows indents them
              // under their supporter.
              columnKey: "gutter",
              header: "",
              cell: () => null,
              width: "32px",
              css: { textAlign: "center" },
            },
            // Sorting is off for the columns whose summary cell says something else (the supporter name,
            // the session count) or that a filter already covers — sorting those only shuffles the groups.
            {
              header: "Description",
              cell: row => (
                <Flex css={{ items: "center", gap: 6 }}>
                  {row.source_type === "claim" ? (
                    <Span title="Out-of-network claim" css={{ color: colors.text2, display: "inline-flex" }}>
                      <ReceiptIcon width="15" height="15" />
                    </Span>
                  ) : (
                    <Span title="In-network session" css={{ color: colors.text2, display: "inline-flex" }}>
                      <CalendarIcon width="15" height="15" />
                    </Span>
                  )}
                  <Truncate width={120} text={row.description}>
                    {row.source_type === "claim" && row.claim_id ? (
                      <StyledLink to="/admin/claims/$claimId" params={{ claimId: row.claim_id }}>
                        {row.description}
                      </StyledLink>
                    ) : (
                      row.description
                    )}
                  </Truncate>
                </Flex>
              ),
            },
            {
              header: "Provider",
              cell: row => (
                <Truncate width={200} text={row.third_party_name}>
                  {/* Claim vendors are free text on the claim, not supplier records, so there is nothing to link to. */}
                  {row.third_party_id ? (
                    <StyledLink to="/admin/suppliers/$supplierId" params={{ supplierId: row.third_party_id }}>
                      {row.third_party_name}
                    </StyledLink>
                  ) : (
                    row.third_party_name
                  )}
                </Truncate>
              ),
            },
            {
              header: "Currency",
              cell: row =>
                row.currency === row.native_currency ? (
                  row.currency.toUpperCase()
                ) : (
                  // Consolidated draw: billed in the borrow currency, incurred in its own.
                  <Span
                    title={`Billed in ${row.currency.toUpperCase()}, incurred in ${row.native_currency.toUpperCase()}`}
                  >
                    {row.currency.toUpperCase()} ← {row.native_currency.toUpperCase()}
                  </Span>
                ),
            },
            {
              header: "Cost Price",
              cell: row => formatDollars(row.our_cost, row.currency.toUpperCase()),
              getValue: row => row.our_cost,
              sortingFn: "number",
              enableSorting: true,
              css: { textAlign: "right" },
            },
            {
              header: "Sale Price",
              cell: row => (
                <Span
                  title={
                    row.admin_fee > 0
                      ? `${formatDollars(row.resale_price - row.admin_fee, row.currency.toUpperCase())} + ${formatDollars(row.admin_fee, row.currency.toUpperCase())} admin fee`
                      : undefined
                  }
                >
                  {formatDollars(row.resale_price, row.currency.toUpperCase())}
                </Span>
              ),
              getValue: row => row.resale_price,
              sortingFn: "number",
              enableSorting: true,
              css: { textAlign: "right" },
            },
            {
              header: "Fees",
              cell: row => (row.transfer_fee > 0 ? formatDollars(row.transfer_fee, row.currency.toUpperCase()) : "—"),
              getValue: row => row.transfer_fee,
              sortingFn: "number",
              enableSorting: true,
              css: { textAlign: "right" },
            },
            {
              columnKey: "transaction-date",
              header: (
                <HeaderWithHint hint="When the spend was recorded: the purchase date for an in-network session, or the date the member submitted the claim.">
                  Created
                </HeaderWithHint>
              ),
              cell: row => formatRowDate(row.event_time),
              getValue: row => new Date(row.event_time).getTime(),
              sortingFn: "number",
              enableSorting: true,
            },
            {
              columnKey: "service-date",
              header: (
                <HeaderWithHint hint="When the session happened: the booking start for an in-network session, or the receipt date on a claim.">
                  Session Date
                </HeaderWithHint>
              ),
              cell: row => (row.service_date ? formatRowDate(row.service_date) : "N/A"),
              getValue: row => (row.service_date ? new Date(row.service_date).getTime() : 0),
              sortingFn: "number",
              enableSorting: true,
            },
            {
              columnKey: "paid-out",
              header: "Paid out?",
              // The payout invoice hangs off the tick rather than earning a column of its own.
              cell: row => (
                <PaidTick
                  paid={row.third_party_paid}
                  label={
                    row.third_party_invoice_friendly_id
                      ? `Paid on payout invoice ${row.third_party_invoice_friendly_id}`
                      : row.source_type === "claim"
                        ? "Paid to the member"
                        : "Paid to the supplier — scheduled for the next payment run"
                  }
                  href={row.third_party_invoice_link}
                />
              ),
              css: { textAlign: "center" },
            },
            {
              columnKey: "repaid",
              header: "Repaid?",
              cell: row => <PaidTick paid={row.supporter_paid} label="Repaid by the supporter" />,
              css: { textAlign: "center" },
            },
            {
              columnKey: "supporter-invoice",
              header: "Invoice",
              cell: row => {
                return !!row.supporter_invoice_friendly_id && !!row.supporter_invoice_link ? (
                  <a href={row.supporter_invoice_link} target="_blank" rel="noreferrer">
                    <Span css={{ color: colors.blue600, weight: 500 }}>{row.supporter_invoice_friendly_id}</Span>
                  </a>
                ) : (
                  <Span title="Scheduled for the next invoice" css={{ color: colors.text2 }}>
                    Pending
                  </Span>
                );
              },
              getValue: row => row.supporter_invoice_friendly_id ?? 0,
              sortingFn: "number",
              enableSorting: true,
            },
          ]}
          dataKey={row => row.usage_id}
          groupBy={{
            key: row => row.supporter_id,
            summary: supporterRecords => summariseSupporter(supporterRecords),
          }}
        />
      </DataTable>
    </>
  );

  function commit(filters: Partial<StoredFilters>) {
    navigate({
      to: ".",
      params: true,
      search: {
        ...search,
        start: search.start?.toISOString(),
        end: search.end?.toISOString(),
        ...filters,
      },
    });
  }

  /** Click a currency to see only that currency; click the one you are already isolated on to go back. */
  function handleCurrencyClick(currency: string) {
    const isOnlySelection = currencies?.length === 1 && currencies[0] === currency;
    commit({ currencies: isOnlySelection ? allCurrencyIds : [currency] });
  }
}

const CURRENT_YEAR = new Date().getFullYear();

/** dd-MMM, with the year only when it isn't this one — most rows are recent, so most rows lose it. */
function formatRowDate(value: string) {
  const date = new Date(value);
  const short = formatShortDate(date);

  return date.getFullYear() === CURRENT_YEAR ? short.slice(0, short.lastIndexOf("-")) : short;
}

/**
 * Paid reads as a tick and unpaid as nothing at all, so a column of arrears scans as the gaps in it.
 * Where there is an invoice behind the payment, the tick is the link to it.
 */
function PaidTick({ paid, label, href }: { paid: boolean; label: string; href?: string | null }) {
  if (!paid) return null;

  const tick = (
    <Span title={label} css={{ color: href ? colors.blue600 : colors.green600, display: "inline-flex" }}>
      <CheckIcon width="14" height="14" />
    </Span>
  );

  return href ? (
    <a href={href} target="_blank" rel="noreferrer">
      {tick}
    </a>
  ) : (
    tick
  );
}

/**
 * Closes each supporter's block with what they owe. Cells are positional — they line up with the
 * column list — and there is one row per currency in the group, because two currencies cannot be
 * added together into a single total.
 */
function summariseSupporter(records: EapArrearDetails[]): (ReactNode | undefined)[][] {
  const byCurrency = new Map<
    string,
    { sessions: number; cost: number; sale: number; fees: number; paidOut: number; repaid: number }
  >();

  for (const record of records) {
    const totals = byCurrency.get(record.currency) ?? {
      sessions: 0,
      cost: 0,
      sale: 0,
      fees: 0,
      paidOut: 0,
      repaid: 0,
    };

    totals.sessions += 1;
    totals.cost += record.our_cost;
    totals.sale += record.resale_price;
    totals.fees += record.transfer_fee;
    totals.paidOut += record.third_party_paid ? 1 : 0;
    totals.repaid += record.supporter_paid ? 1 : 0;

    byCurrency.set(record.currency, totals);
  }

  const [supporter] = records;

  return [...byCurrency.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, totals], index) => {
      const code = currency.toUpperCase();

      // Positional, in column order: type, description, 3rd party, currency, cost, sale, fees, dates,
      // paid out, repaid, invoice.
      return [
        undefined,
        index === 0 && supporter ? (
          <Truncate width={160} text={supporter.supporter_name}>
            <StyledLink to="/admin/supporters/$supporterId" params={{ supporterId: supporter.supporter_id }}>
              {supporter.supporter_name}
            </StyledLink>
          </Truncate>
        ) : undefined,
        `${totals.sessions} ${totals.sessions === 1 ? "session" : "sessions"}`,
        code,
        formatDollars(totals.cost, code),
        formatDollars(totals.sale, code),
        totals.fees > 0 ? formatDollars(totals.fees, code) : "—",
        undefined,
        undefined,
        <Ratio key="paid-out" count={totals.paidOut} total={totals.sessions} label="paid out" />,
        <Ratio key="repaid" count={totals.repaid} total={totals.sessions} label="repaid by the supporter" />,
        undefined,
      ];
    });
}

/** How much of a collapsed group is already settled, without having to open it. */
function Ratio({ count, total, label }: { count: number; total: number; label: string }) {
  return (
    <Span title={`${count} of ${total} ${label}`} css={{ color: count === total ? colors.green600 : colors.text2 }}>
      {count}/{total}
    </Span>
  );
}

/** Names and descriptions run long; one line with an ellipsis keeps the row count on screen. */
function Truncate({ width, text, children }: PropsWithChildren<{ width: number; text: string }>) {
  return (
    <Span
      title={text}
      css={{
        display: "block",
        maxWidth: width,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Span>
  );
}

/** A column heading whose meaning differs between purchases and claims, so it carries the explanation. */
function HeaderWithHint({ hint, children }: PropsWithChildren<{ hint: string }>) {
  return (
    <Span title={hint} css={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      {children}
      <InformationCircleIcon width="11" height="11" color={colors.text2} />
    </Span>
  );
}

/**
 * Filter buttons say what is currently selected rather than what they filter on, so the current view is
 * readable without opening each dropdown. "All" beats listing every option back at the reader.
 */
function describeSelection(subject: string, selected: string[], options: IdAndName[]) {
  if (options.length === 0 || selected.length >= options.length) return `${subject}: All`;
  if (selected.length === 0) return `${subject}: None`;

  const names = options.filter(option => selected.includes(option.id)).map(option => option.name);
  return names.length <= 2 ? `${subject}: ${names.join(", ")}` : `${subject}: ${names.length} of ${options.length}`;
}

function describePaymentSelection(subject: string, selected: string[]) {
  const paid = selected.includes("paid");
  const unpaid = selected.includes("unpaid");

  if (paid && unpaid) return `${subject} Paid & Unpaid`;
  if (paid) return `${subject} Paid only`;
  if (unpaid) return `${subject} Unpaid only`;
  return `${subject}: None`;
}

function describeDateRange(start: Date | undefined, end: Date | undefined) {
  if (start && end) return undefined; // The picker prints the range (or its preset name) itself.
  if (start) return `Date Range: from ${formatShortDate(start)}`;
  if (end) return `Date Range: until ${formatShortDate(end)}`;
  return "Date Range: All";
}

type CurrencyTotalsProps = {
  records: EapArrearDetails[];
  selectedCurrencies: string[];
  onCurrencyClick: (currency: string) => void;
};

/**
 * Amounts never cross currencies: each row is billed and settled in its own currency (the borrow
 * currency for consolidated draws), so totals are per currency rather than one grand total.
 */
function CurrencyTotals({ records, selectedCurrencies, onCurrencyClick }: CurrencyTotalsProps) {
  const totalsByCurrency = new Map<
    string,
    { rows: number; ourCost: number; resale: number; transferFee: number; unbilled: number }
  >();

  for (const record of records) {
    const totals = totalsByCurrency.get(record.currency) ?? {
      rows: 0,
      ourCost: 0,
      resale: 0,
      transferFee: 0,
      unbilled: 0,
    };

    totals.rows += 1;
    totals.ourCost += record.our_cost;
    totals.resale += record.resale_price;
    totals.transferFee += record.transfer_fee;
    totals.unbilled += record.supporter_paid ? 0 : record.resale_price + record.transfer_fee;

    totalsByCurrency.set(record.currency, totals);
  }

  if (totalsByCurrency.size === 0) return null;

  return (
    <Flex css={{ gap: 12, flexWrap: "wrap" }}>
      {Array.from(totalsByCurrency.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([currency, totals]) => {
          const code = currency.toUpperCase();
          const isIsolated = selectedCurrencies.length === 1 && selectedCurrencies[0] === currency;
          const isShown = selectedCurrencies.includes(currency);

          return (
            <UnstyledButton
              key={currency}
              onClick={() => onCurrencyClick(currency)}
              title={isIsolated ? `Show all currencies again` : `Show only ${code}`}
              css={{
                textAlign: "left",
                cursor: "pointer",
                backgroundColor: "white",
                rounded: 12,
                px: 16,
                py: 12,
                opacity: isShown ? 1 : 0.55,
                boxShadow: `inset 0 0 0 ${isIsolated ? 2 : 1}px ${isIsolated ? colors.blue600 : colors.outline2}`,
              }}
            >
              <Text css={{ weight: 600 }}>
                {code} · {totals.rows} {totals.rows === 1 ? "row" : "rows"}
              </Text>
              <Text css={{ fontScale: "sm", color: colors.text2 }}>
                Cost {formatDollars(totals.ourCost, code)} · Sale {formatDollars(totals.resale, code)}
                {totals.transferFee > 0 && <> · Transfer fees {formatDollars(totals.transferFee, code)}</>}
              </Text>
              <Text css={{ fontScale: "sm", color: colors.text2 }}>
                Not yet paid by supporter {formatDollars(totals.unbilled, code)}
              </Text>
            </UnstyledButton>
          );
        })}
    </Flex>
  );
}

function searchFilter(
  supporterIds: string[] | undefined,
  sources: string[],
  currencies: string[] | undefined,
  thirdPartyPayment: string[],
  supporterPayment: string[],
  start: Date | undefined,
  end: Date | undefined,
): (value: EapArrearDetails) => boolean {
  // Copy before shifting to the end of the day: mutating the picker's own Date would creep the filter
  // forward on every pass.
  const endOfDay = end && new Date(new Date(end).setHours(23, 59, 59, 999));

  return record => {
    if (!sources.includes(record.source_type)) {
      return false;
    }

    if (supporterIds && !supporterIds.includes(record.supporter_id)) {
      return false;
    }

    if (currencies && !currencies.includes(record.currency)) {
      return false;
    }

    const third_party_payments = record.third_party_paid ? ["paid"] : ["unpaid"];
    const supporter_payments = record.supporter_paid ? ["paid"] : ["unpaid"];

    if (thirdPartyPayment && !thirdPartyPayment.filter(el => third_party_payments.includes(el)).length) {
      return false;
    }

    if (supporterPayment && !supporterPayment.filter(el => supporter_payments.includes(el)).length) {
      return false;
    }

    const event_time = new Date(record.event_time);
    if (start && event_time < start) {
      return false;
    }

    if (endOfDay && event_time > endOfDay) {
      return false;
    }

    return true;
  };
}
